import { BOARD, MIN_AUCTION_BID } from "@grundbesitz/shared";
import type { EngineAction, EngineError, StateView, TradeSide } from "@grundbesitz/shared";
import {
  activePlayers,
  checkBuildRules,
  checkMortgageRules,
  checkSellRules,
  fieldGroupHasBuildings,
  getPlayer,
  isHopeless,
  ownableAt,
  unmortgageCost,
} from "./util";

const err = (code: EngineError["code"], message: string): EngineError => ({ code, message });

const ASSET_ACTIONS = new Set(["BUILD", "SELL_BUILDING", "MORTGAGE", "UNMORTGAGE"]);
const TRADE_ACTIONS = new Set(["PROPOSE_TRADE", "ACCEPT_TRADE", "COUNTER_TRADE"]);

/** Smallest bid currently accepted in the running auction. */
export function minimumBid(s: StateView): number {
  const a = s.auction;
  if (!a) return MIN_AUCTION_BID;
  let high = 0;
  for (const b of a.bids) high = Math.max(high, b.amount);
  return high === 0 ? MIN_AUCTION_BID : high + 1;
}

export function highestBid(s: StateView): { playerId: string; amount: number } | null {
  const a = s.auction;
  if (!a || a.bids.length === 0) return null;
  let best = a.bids[0];
  for (const b of a.bids) if (b.amount > best.amount) best = b;
  return best;
}

/** Bank supply needed for the next build step on this street. */
export function supplyFor(s: StateView, kind: "house" | "hotel"): number {
  return kind === "house" ? s.bank.houses : s.bank.hotels;
}

function tradeSideError(s: StateView, ownerId: string, side: TradeSide): string | null {
  const owner = getPlayer(s, ownerId)!;
  if (side.cash > owner.cash) return `${owner.name} hat nicht genug Bargeld.`;
  if (new Set(side.fields).size !== side.fields.length) return "Ein Grundstück ist doppelt aufgeführt.";
  for (const f of side.fields) {
    if (!ownableAt(f)) return "Ungültiges Grundstück im Handel.";
    if (s.properties[f].owner !== ownerId) return `${BOARD[f].name} gehört ${owner.name} nicht.`;
    if (fieldGroupHasBuildings(s, f)) {
      return `Auf der Farbgruppe von ${BOARD[f].name} stehen Gebäude. Sie müssen erst an die Bank verkauft werden.`;
    }
  }
  if (new Set(side.jailCards).size !== side.jailCards.length) return "Eine Freikarte ist doppelt aufgeführt.";
  for (const c of side.jailCards) {
    if (!owner.jailCards.includes(c)) return `${owner.name} besitzt diese Freikarte nicht.`;
  }
  return null;
}

/** Same checks as `tradeSideError` but without cash, used to invalidate stale offers. */
export function tradeAssetsValid(s: StateView, ownerId: string, side: TradeSide): boolean {
  const owner = getPlayer(s, ownerId);
  if (!owner || owner.eliminated) return false;
  for (const f of side.fields) {
    if (!ownableAt(f) || s.properties[f].owner !== ownerId || fieldGroupHasBuildings(s, f)) return false;
  }
  return side.jailCards.every((c) => owner.jailCards.includes(c));
}

const emptySide = (side: TradeSide) => side.cash === 0 && side.fields.length === 0 && side.jailCards.length === 0;

/**
 * Checks whether `action` is legal in `state`. Pure and free of hidden data, so the client
 * uses it too to enable or disable buttons. The server is still the authority.
 */
export function validateAction(s: StateView, action: EngineAction): EngineError | null {
  if (s.phase === "GAME_OVER") return err("GAME_OVER", "Das Spiel ist beendet.");
  if (action.expectedVersion !== undefined && action.expectedVersion !== s.version) {
    return err("VERSION_CONFLICT", "Der Spielstand hat sich geändert. Bitte erneut versuchen.");
  }

  if (action.player === "system") {
    switch (action.type) {
      case "SYSTEM_CLOSE_AUCTION":
        if (!s.auction || s.auction.id !== action.auctionId || s.auction.activity !== action.activity) {
          return err("INVALID", "Die Versteigerung hat sich seit dem Timer geändert.");
        }
        return null;
      case "SYSTEM_CLOSE_BUILD_WINDOW":
        if (!s.buildWindow || s.buildWindow.closing) return err("INVALID", "Kein Bau-Anmeldefenster offen.");
        return null;
      case "SYSTEM_TIME_LIMIT":
        return null;
      default:
        return err("NOT_ALLOWED", "Systemaktion nicht erlaubt.");
    }
  }

  const actor = getPlayer(s, action.player);
  if (!actor) return err("UNKNOWN_PLAYER", "Unbekannter Spieler.");
  if (actor.eliminated) return err("NOT_ALLOWED", "Du bist ausgeschieden.");
  const isTurn = s.turn.playerId === actor.id;

  const type = action.type;

  // Locks for asset actions and trades.
  if (ASSET_ACTIONS.has(type) || TRADE_ACTIONS.has(type)) {
    if (s.auction) return err("LOCKED", "Während einer Versteigerung ist das nicht möglich.");
    if (s.mortgageDecisions.length > 0) {
      return err("LOCKED", "Erst muss über die übernommene Hypothek entschieden werden.");
    }
    if (s.debts.length > 0) {
      const debtors = new Set(s.debts.map((d) => d.debtor));
      if (TRADE_ACTIONS.has(type)) {
        let involved = debtors.has(actor.id);
        if (action.type === "PROPOSE_TRADE" && debtors.has(action.to)) involved = true;
        if (action.type === "ACCEPT_TRADE") {
          const t = s.trades.find((x) => x.id === action.tradeId);
          if (t && debtors.has(t.from)) involved = true;
        }
        if (action.type === "COUNTER_TRADE") {
          const t = s.trades.find((x) => x.id === action.tradeId);
          if (t && debtors.has(t.from)) involved = true;
        }
        if (!involved) return err("LOCKED", "Ein Spieler muss erst seine Schulden begleichen.");
      } else if (!debtors.has(actor.id)) {
        return err("LOCKED", "Ein Spieler muss erst seine Schulden begleichen.");
      }
    }
  }

  switch (action.type) {
    case "ROLL_DICE": {
      if (!isTurn) return err("NOT_YOUR_TURN", "Du bist nicht am Zug.");
      if (s.phase !== "AWAIT_ROLL" && s.phase !== "IN_JAIL_DECISION") {
        return err("WRONG_PHASE", "Jetzt kannst du nicht würfeln.");
      }
      return null;
    }
    case "BUY_PROPERTY": {
      if (s.phase !== "BUY_DECISION" || s.pending?.kind !== "BUY") return err("WRONG_PHASE", "Es gibt nichts zu kaufen.");
      if (s.pending.playerId !== actor.id) return err("NOT_YOUR_TURN", "Du bist nicht am Zug.");
      const f = ownableAt(s.pending.field)!;
      if (actor.cash < f.price) return err("NOT_ENOUGH_CASH", "Dafür fehlt dir das Geld.");
      return null;
    }
    case "DECLINE_PURCHASE": {
      if (s.phase !== "BUY_DECISION" || s.pending?.kind !== "BUY") return err("WRONG_PHASE", "Es gibt nichts zu kaufen.");
      if (s.pending.playerId !== actor.id) return err("NOT_YOUR_TURN", "Du bist nicht am Zug.");
      return null;
    }
    case "CHOOSE_CARD_OPTION": {
      if (s.phase !== "CARD_CHOICE" || s.pending?.kind !== "CARD_CHOICE") return err("WRONG_PHASE", "Keine Kartenwahl offen.");
      if (s.pending.playerId !== actor.id) return err("NOT_YOUR_TURN", "Du bist nicht am Zug.");
      return null;
    }
    case "END_TURN": {
      if (!isTurn) return err("NOT_YOUR_TURN", "Du bist nicht am Zug.");
      if (s.phase !== "AWAIT_END_TURN") return err("WRONG_PHASE", "Du kannst den Zug jetzt nicht beenden.");
      return null;
    }
    case "PAY_JAIL_FINE": {
      if (!isTurn || s.phase !== "IN_JAIL_DECISION") return err("WRONG_PHASE", "Du bist nicht im Gefängnis am Zug.");
      if (actor.cash < 50) return err("NOT_ENOUGH_CASH", "Dir fehlen 50 M für die Kaution.");
      return null;
    }
    case "USE_JAIL_CARD": {
      if (!isTurn || s.phase !== "IN_JAIL_DECISION") return err("WRONG_PHASE", "Du bist nicht im Gefängnis am Zug.");
      if (actor.jailCards.length === 0) return err("INVALID", "Du hast keine Freikarte.");
      return null;
    }
    case "BID": {
      const a = s.auction;
      if (!a) return err("WRONG_PHASE", "Es läuft keine Versteigerung.");
      if (!a.eligible.includes(actor.id)) return err("NOT_ALLOWED", "Du darfst hier nicht mitbieten.");
      if (a.passed.includes(actor.id)) return err("NOT_ALLOWED", "Du hast bereits verzichtet.");
      if (s.debts.some((d) => d.debtor === actor.id)) return err("NOT_ALLOWED", "Mit Schulden darfst du nicht bieten.");
      const high = highestBid(s);
      if (high && high.playerId === actor.id) return err("INVALID", "Du bietest bereits am höchsten.");
      if (action.amount < minimumBid(s)) return err("INVALID", `Das Gebot muss mindestens ${minimumBid(s)} M betragen.`);
      if (action.amount > actor.cash) return err("NOT_ENOUGH_CASH", "Du kannst nicht mehr bieten, als du Bargeld hast.");
      return null;
    }
    case "PASS_AUCTION": {
      const a = s.auction;
      if (!a) return err("WRONG_PHASE", "Es läuft keine Versteigerung.");
      if (!a.eligible.includes(actor.id)) return err("NOT_ALLOWED", "Du nimmst nicht an der Versteigerung teil.");
      if (a.passed.includes(actor.id)) return err("INVALID", "Du hast bereits verzichtet.");
      const high = highestBid(s);
      if (high && high.playerId === actor.id) return err("INVALID", "Du bietest bereits am höchsten.");
      return null;
    }
    case "CLAIM_RENT": {
      if (!s.rentClaim || s.rentClaim.owner !== actor.id) return err("INVALID", "Du hast keine Miete zu fordern.");
      return null;
    }
    case "MORTGAGE_DECISION": {
      const d = s.mortgageDecisions.find((x) => x.playerId === actor.id && x.field === action.field);
      if (!d) return err("INVALID", "Für dieses Grundstück steht keine Entscheidung an.");
      if (action.choice === "UNMORTGAGE" && actor.cash < unmortgageCost(action.field)) {
        return err("NOT_ENOUGH_CASH", "Zum Auflösen fehlt dir das Geld.");
      }
      return null;
    }
    case "BUILD": {
      if (s.debts.some((d) => d.debtor === actor.id)) return err("LOCKED", "Mit Schulden darfst du nicht bauen.");
      const check = checkBuildRules(s, actor.id, action.field);
      if (!check.ok) return err("NOT_ALLOWED", check.reason!);
      if (actor.cash < check.cost) return err("NOT_ENOUGH_CASH", "Zum Bauen fehlt dir das Geld.");
      if (s.buildWindow) {
        if (s.buildWindow.closing) return err("LOCKED", "Die Bauplätze werden gerade versteigert.");
        if (s.buildWindow.kind !== check.kind) {
          return err("LOCKED", "Gerade läuft ein Bau-Anmeldefenster. Bitte kurz warten.");
        }
        if (s.buildWindow.requests.some((r) => r.playerId === actor.id && r.field === action.field)) {
          return err("INVALID", "Du hast dich für diese Straße schon angemeldet.");
        }
      }
      const supply = supplyFor(s, check.kind);
      if (supply < 1) {
        return err("NOT_ALLOWED", check.kind === "house" ? "Die Bank hat keine Häuser mehr." : "Die Bank hat keine Hotels mehr.");
      }
      return null;
    }
    case "SELL_BUILDING": {
      const check = checkSellRules(s, actor.id, action.field);
      if (!check.ok) return err("NOT_ALLOWED", check.reason!);
      return null;
    }
    case "MORTGAGE": {
      const check = checkMortgageRules(s, actor.id, action.field);
      if (!check.ok) return err("NOT_ALLOWED", check.reason!);
      return null;
    }
    case "UNMORTGAGE": {
      if (s.debts.some((d) => d.debtor === actor.id)) return err("LOCKED", "Mit Schulden darfst du keine Hypothek ablösen.");
      const f = ownableAt(action.field);
      if (!f) return err("INVALID", "Ungültiges Feld.");
      const prop = s.properties[action.field];
      if (prop.owner !== actor.id) return err("NOT_ALLOWED", "Das Grundstück gehört dir nicht.");
      if (!prop.mortgaged) return err("NOT_ALLOWED", "Das Grundstück ist nicht belastet.");
      if (actor.cash < unmortgageCost(action.field)) return err("NOT_ENOUGH_CASH", "Zum Auflösen fehlt dir das Geld.");
      return null;
    }
    case "PROPOSE_TRADE": {
      const to = getPlayer(s, action.to);
      if (!to || to.eliminated) return err("INVALID", "Der Mitspieler ist nicht im Spiel.");
      if (to.id === actor.id) return err("INVALID", "Du kannst nicht mit dir selbst handeln.");
      if (emptySide(action.give) && emptySide(action.get)) return err("INVALID", "Das Angebot ist leer.");
      if (s.trades.filter((t) => t.from === actor.id).length >= 10) {
        return err("INVALID", "Du hast zu viele offene Angebote.");
      }
      const e1 = tradeSideError(s, actor.id, action.give);
      if (e1) return err("INVALID", e1);
      const e2 = tradeSideError(s, to.id, action.get);
      if (e2) return err("INVALID", e2);
      return null;
    }
    case "ACCEPT_TRADE": {
      const t = s.trades.find((x) => x.id === action.tradeId);
      if (!t) return err("INVALID", "Das Angebot existiert nicht mehr.");
      if (t.to !== actor.id) return err("NOT_ALLOWED", "Das Angebot ist nicht an dich gerichtet.");
      const e1 = tradeSideError(s, t.from, t.give);
      if (e1) return err("INVALID", e1);
      const e2 = tradeSideError(s, t.to, t.get);
      if (e2) return err("INVALID", e2);
      return null;
    }
    case "REJECT_TRADE": {
      const t = s.trades.find((x) => x.id === action.tradeId);
      if (!t) return err("INVALID", "Das Angebot existiert nicht mehr.");
      if (t.to !== actor.id) return err("NOT_ALLOWED", "Das Angebot ist nicht an dich gerichtet.");
      return null;
    }
    case "CANCEL_TRADE": {
      const t = s.trades.find((x) => x.id === action.tradeId);
      if (!t) return err("INVALID", "Das Angebot existiert nicht mehr.");
      if (t.from !== actor.id) return err("NOT_ALLOWED", "Das ist nicht dein Angebot.");
      return null;
    }
    case "COUNTER_TRADE": {
      const t = s.trades.find((x) => x.id === action.tradeId);
      if (!t) return err("INVALID", "Das Angebot existiert nicht mehr.");
      if (t.to !== actor.id) return err("NOT_ALLOWED", "Das Angebot ist nicht an dich gerichtet.");
      if (emptySide(action.give) && emptySide(action.get)) return err("INVALID", "Das Angebot ist leer.");
      const e1 = tradeSideError(s, actor.id, action.give);
      if (e1) return err("INVALID", e1);
      const e2 = tradeSideError(s, t.from, action.get);
      if (e2) return err("INVALID", e2);
      return null;
    }
    case "DECLARE_BANKRUPTCY": {
      const owed = s.debts.filter((d) => d.debtor === actor.id);
      if (owed.length === 0) return err("INVALID", "Du hast keine Schulden.");
      if (!isHopeless(s, actor.id)) {
        return err("NOT_ALLOWED", "Du kannst deine Schulden noch durch Verkäufe oder Hypotheken decken.");
      }
      return null;
    }
    case "RESIGN": {
      if (activePlayers(s).length < 2) return err("INVALID", "Du bist der letzte Spieler.");
      return null;
    }
    default:
      return err("INVALID", "Unbekannte Aktion.");
  }
}
